/**
 * SEO & Analytics Composable for dynamic meta tag management and GA4 tracking
 * 
 * Features:
 * - Dynamic meta tag updates (title, description, OG, Twitter)
 * - JSON-LD structured data injection
 * - GA4 page view tracking with custom dimensions
 * - SEO validation (title/description length, duplicate detection)
 * - TypeScript interfaces for all options
 * 
 * Use this in page components to set page-specific SEO metadata.
 * The base SEO in index.html provides defaults for the landing page.
 */

import { toIsoDuration } from '@/utils/duration'

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface SeoOptions {
  /** Page title (50-60 chars recommended) */
  title?: string
  /** Meta description (120-155 chars recommended) */
  description?: string
  /** Comma-separated keywords */
  keywords?: string
  /** Canonical URL path (e.g., '/discover') */
  canonicalPath?: string
  /** Open Graph title (defaults to title) */
  ogTitle?: string
  /** Open Graph description (defaults to description) */
  ogDescription?: string
  /** Open Graph image URL (1200x630 recommended) */
  ogImage?: string
  /** Open Graph content type */
  ogType?: 'website' | 'article' | 'product'
  /** Twitter card type */
  twitterCard?: 'summary' | 'summary_large_image'
  /** Prevent search engine indexing */
  noIndex?: boolean
  /** JSON-LD structured data object */
  structuredData?: object
}

export interface AnalyticsPageView {
  /** Page title for analytics */
  page_title: string
  /** Page location/path */
  page_location: string
  /** Content group for categorization */
  content_group?: string
  /** User's subscription status */
  user_tier?: 'free' | 'premium'
  /** Whether user is logged in */
  logged_in?: boolean
}

export interface SeoValidationResult {
  isValid: boolean
  warnings: string[]
  errors: string[]
  suggestions: string[]
}

// ============================================================================
// Constants
// ============================================================================

export const BASE_URL = 'https://shelf.recipes'
export const DEFAULT_IMAGE = `${BASE_URL}/img/og-image.png`
export const SITE_NAME = 'Shelf'

// SEO best practice limits
const SEO_LIMITS = {
  title: { min: 30, ideal: 50, max: 60 },
  description: { min: 70, ideal: 120, max: 155 }
}

// Track titles for duplicate detection
const usedTitles = new Set<string>()

// ============================================================================
// Main Composable
// ============================================================================

/**
 * SEO and Analytics management composable
 * 
 * @example
 * ```ts
 * const { setSeo, trackPageView, validateSeo } = useSeo()
 * 
 * onMounted(() => {
 *   const seoConfig = {
 *     title: 'Discover Recipes',
 *     description: 'Browse thousands of recipes...',
 *     canonicalPath: '/discover'
 *   }
 *   
 *   // Validate before applying (optional)
 *   const validation = validateSeo(seoConfig)
 *   if (validation.warnings.length) {
 *     console.warn('SEO warnings:', validation.warnings)
 *   }
 *   
 *   setSeo(seoConfig)
 *   trackPageView({ page_title: 'Discover', content_group: 'recipes' })
 * })
 * ```
 */
export function useSeo() {

  // --------------------------------------------------------------------------
  // Private Helpers
  // --------------------------------------------------------------------------

  const setMeta = (name: string, content: string, property = false) => {
    const attr = property ? 'property' : 'name'
    let element = document.querySelector(`meta[${attr}="${name}"]`)

    if (element) {
      element.setAttribute('content', content)
    } else {
      element = document.createElement('meta')
      element.setAttribute(attr, name)
      element.setAttribute('content', content)
      document.head.appendChild(element)
    }
  }

  const setCanonical = (path: string) => {
    const url = `${BASE_URL}${path}`
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement

    if (link) {
      link.href = url
    } else {
      link = document.createElement('link')
      link.rel = 'canonical'
      link.href = url
      document.head.appendChild(link)
    }
  }

  // --------------------------------------------------------------------------
  // Structured Data Management
  // --------------------------------------------------------------------------

  const setStructuredData = (data: object, id = 'page-structured-data') => {
    let script = document.getElementById(id) as HTMLScriptElement

    if (script) {
      script.textContent = JSON.stringify(data)
    } else {
      script = document.createElement('script')
      script.id = id
      script.type = 'application/ld+json'
      script.textContent = JSON.stringify(data)
      document.head.appendChild(script)
    }
  }

  const removeStructuredData = (id = 'page-structured-data') => {
    const script = document.getElementById(id)
    if (script) {
      script.remove()
    }
  }

  // --------------------------------------------------------------------------
  // SEO Validation
  // --------------------------------------------------------------------------

  /**
   * Validate SEO configuration against best practices
   */
  const validateSeo = (options: SeoOptions): SeoValidationResult => {
    const warnings: string[] = []
    const errors: string[] = []
    const suggestions: string[] = []

    // Title validation
    if (options.title) {
      const titleLength = options.title.length

      if (titleLength < SEO_LIMITS.title.min) {
        warnings.push(`Title is too short (${titleLength} chars). Aim for ${SEO_LIMITS.title.ideal}+ chars.`)
      } else if (titleLength > SEO_LIMITS.title.max) {
        errors.push(`Title is too long (${titleLength} chars). Max ${SEO_LIMITS.title.max} chars to avoid truncation.`)
      } else if (titleLength < SEO_LIMITS.title.ideal) {
        suggestions.push(`Title could be longer (${titleLength} chars). Ideal: ${SEO_LIMITS.title.ideal}-${SEO_LIMITS.title.max} chars.`)
      }

      // Check for duplicate titles
      if (usedTitles.has(options.title)) {
        warnings.push(`Duplicate title detected: "${options.title}"`)
      }
    } else {
      errors.push('Title is required for SEO.')
    }

    // Description validation
    if (options.description) {
      const descLength = options.description.length

      if (descLength < SEO_LIMITS.description.min) {
        warnings.push(`Description is too short (${descLength} chars). Aim for ${SEO_LIMITS.description.ideal}+ chars.`)
      } else if (descLength > SEO_LIMITS.description.max) {
        errors.push(`Description is too long (${descLength} chars). Max ${SEO_LIMITS.description.max} chars.`)
      } else if (descLength < SEO_LIMITS.description.ideal) {
        suggestions.push(`Description could be longer (${descLength} chars). Ideal: ${SEO_LIMITS.description.ideal}-${SEO_LIMITS.description.max} chars.`)
      }
    } else {
      warnings.push('Description is recommended for SEO.')
    }

    // Canonical URL validation
    if (!options.canonicalPath) {
      suggestions.push('Consider adding a canonical URL to prevent duplicate content issues.')
    }

    // OG Image validation
    if (!options.ogImage) {
      suggestions.push('Adding an OG image improves social media sharing appearance.')
    }

    return {
      isValid: errors.length === 0,
      warnings,
      errors,
      suggestions
    }
  }

  // --------------------------------------------------------------------------
  // Main SEO Setter
  // --------------------------------------------------------------------------

  /**
   * Set all SEO meta tags for the current page
   */
  const setSeo = (options: SeoOptions) => {
    // Title
    if (options.title) {
      const fullTitle = options.title.includes(SITE_NAME)
        ? options.title
        : `${options.title} | ${SITE_NAME}`
      document.title = fullTitle
      usedTitles.add(options.title)
    }

    // Description
    if (options.description) {
      setMeta('description', options.description)
      setMeta('og:description', options.ogDescription || options.description, true)
      setMeta('twitter:description', options.description)
    }

    // Keywords
    if (options.keywords) {
      setMeta('keywords', options.keywords)
    }

    // Canonical URL
    if (options.canonicalPath) {
      setCanonical(options.canonicalPath)
      setMeta('og:url', `${BASE_URL}${options.canonicalPath}`, true)
      setMeta('twitter:url', `${BASE_URL}${options.canonicalPath}`)
    }

    // Open Graph
    if (options.ogTitle) {
      setMeta('og:title', options.ogTitle, true)
      setMeta('twitter:title', options.ogTitle)
    } else if (options.title) {
      setMeta('og:title', options.title, true)
      setMeta('twitter:title', options.title)
    }

    if (options.ogType) {
      setMeta('og:type', options.ogType, true)
    }

    if (options.ogImage) {
      setMeta('og:image', options.ogImage, true)
      setMeta('twitter:image', options.ogImage)
    }

    // Twitter
    if (options.twitterCard) {
      setMeta('twitter:card', options.twitterCard)
    }

    // Robots
    if (options.noIndex) {
      setMeta('robots', 'noindex, nofollow')
    } else {
      setMeta('robots', 'index, follow')
    }

    // Structured data
    if (options.structuredData) {
      setStructuredData(options.structuredData)
    }
  }

  /**
   * Reset to default SEO (useful when leaving a page)
   */
  const resetSeo = () => {
    document.title = 'Shelf - Pantry Management, Recipe Organization & Meal Planning App'
    setMeta('description', 'Shelf tracks your pantry, digitizes recipe cards from photos, organizes recipes into shareable books, and builds shopping lists for what to cook next.')
    setCanonical('/')
    removeStructuredData()
  }

  // --------------------------------------------------------------------------
  // GA4 Analytics Integration
  // --------------------------------------------------------------------------

  /**
   * Track a page view in GA4 with custom dimensions
   * 
   * @example
   * ```ts
   * trackPageView({
   *   page_title: 'Discover Recipes',
   *   page_location: '/discover',
   *   content_group: 'recipes',
   *   user_tier: 'free',
   *   logged_in: false
   * })
   * ```
   */
  const trackPageView = (params: Partial<AnalyticsPageView> = {}) => {
    // Check if gtag is available (GA4)
    if (typeof window !== 'undefined' && 'gtag' in window) {
      const gtag = (window as any).gtag

      gtag('event', 'page_view', {
        page_title: params.page_title || document.title,
        page_location: params.page_location || window.location.href,
        page_path: window.location.pathname,
        content_group: params.content_group,
        user_tier: params.user_tier,
        logged_in: params.logged_in
      })
    }
  }

  /**
   * Track a custom event in GA4
   * 
   * @example
   * ```ts
   * trackEvent('recipe_saved', { recipe_id: '123', source: 'discover' })
   * ```
   */
  const trackEvent = (eventName: string, params: Record<string, any> = {}) => {
    if (typeof window !== 'undefined' && 'gtag' in window) {
      const gtag = (window as any).gtag
      gtag('event', eventName, params)
    }
  }

  /**
   * Set user properties for GA4 (call once after login)
   * 
   * @example
   * ```ts
   * setUserProperties({
  *   user_tier: 'premium',
   *   account_created: '2024-01-15'
   * })
   * ```
   */
  const setUserProperties = (properties: Record<string, any>) => {
    if (typeof window !== 'undefined' && 'gtag' in window) {
      const gtag = (window as any).gtag
      gtag('set', 'user_properties', properties)
    }
  }

  // --------------------------------------------------------------------------
  // Helper Functions
  // --------------------------------------------------------------------------

  /**
   * Generate a mobile-friendly truncated title
   */
  const truncateTitle = (title: string, maxLength = 40): string => {
    if (title.length <= maxLength) return title
    return title.substring(0, maxLength - 3) + '...'
  }

  /**
   * Get character counts for SEO optimization
   */
  const getSeoCharCounts = (options: SeoOptions) => ({
    title: {
      current: options.title?.length || 0,
      ideal: SEO_LIMITS.title.ideal,
      max: SEO_LIMITS.title.max
    },
    description: {
      current: options.description?.length || 0,
      ideal: SEO_LIMITS.description.ideal,
      max: SEO_LIMITS.description.max
    }
  })

  return {
    // Core SEO
    setSeo,
    resetSeo,
    setStructuredData,
    removeStructuredData,
    validateSeo,

    // Analytics
    trackPageView,
    trackEvent,
    setUserProperties,

    // Helpers
    truncateTitle,
    getSeoCharCounts,

    // Constants
    BASE_URL,
    DEFAULT_IMAGE,
    SEO_LIMITS
  }
}

// ============================================================================
// Pre-defined Page SEO Configurations
// ============================================================================

export const PAGE_SEO: Record<string, SeoOptions> = {
  landing: {
    title: 'Shelf - Pantry Management, Recipe Organization & Meal Planning App',
    description: 'Shelf tracks your pantry, digitizes recipe cards from photos, organizes recipes into shareable books, and builds shopping lists for what to cook next.',
    keywords: 'pantry tracker, recipe organizer, meal planning app, what to cook, recipe book app, recipe card scanner, digitize recipes, shopping list generator, kitchen inventory, food management',
    canonicalPath: '/',
    ogType: 'website'
  },

  discover: {
    title: 'Discover Recipes - Find Your Next Favorite Meal',
    description: 'Browse thousands of community recipes. Find inspiration for dinner, discover new cuisines, and save recipes to your personal collection. Filter by ingredients, cuisine, and cooking time.',
    keywords: 'recipe search, find recipes, dinner ideas, recipe discovery, cooking inspiration, recipe browser, community recipes, recipe collection',
    canonicalPath: '/discover',
    ogTitle: 'Discover Delicious Recipes on Shelf',
    ogType: 'website'
  },

  features: {
    title: 'Features - Everything You Need to Simplify Cooking',
    description: 'Explore Shelf features: pantry tracking, recipe books, meal planning, auto shopping lists, recipe-card scanning, production-quality food photos, family sharing, and more.',
    keywords: 'cooking app features, pantry management features, recipe app features, meal planning features, AI recipe generator, recipe scanning, kitchen app',
    canonicalPath: '/features',
    ogTitle: 'Shelf Features - Simplify Your Kitchen',
    ogType: 'website'
  },

  pricing: {
    title: 'Pricing - Free Plan & Premium Subscription',
    description: 'Shelf is free to use with core features including current-week meal planning and recipe scaling. Upgrade to Premium for recurring meal schedules, expanded AI access, production-quality food photos, and public step-by-step recipe photos.',
    keywords: 'shelf pricing, cooking app subscription, recipe app pricing, meal planning cost, kitchen app free, premium cooking app',
    canonicalPath: '/pricing',
    ogTitle: 'Shelf Pricing - Start Free, Go Premium',
    ogType: 'website'
  },

  login: {
    title: 'Sign In - Access Your Pantry & Recipes',
    description: 'Sign in to Shelf to access your pantry inventory, recipe books, and shopping lists. New user? Create a free account in seconds.',
    canonicalPath: '/login',
    ogType: 'website',
    noIndex: true // Don't index login page
  }
}

// ============================================================================
// Content Group Mappings (for GA4)
// ============================================================================

export const CONTENT_GROUPS = {
  marketing: ['/', '/features', '/pricing'],
  recipes: ['/discover', '/recipe'],
  pantry: ['/pantry'],
  shopping: ['/shopping_lists'],
  account: ['/login', '/settings'],
  admin: ['/admin', '/data-migration', '/sandbox']
} as const

/**
 * Get content group for a given path
 */
export function getContentGroup(path: string): string {
  for (const [group, paths] of Object.entries(CONTENT_GROUPS)) {
    if (paths.some(p => path === p || path.startsWith(p + '/'))) {
      return group
    }
  }
  return 'other'
}

// ============================================================================
// Structured Data Generators
// ============================================================================

/**
 * Generate structured data for a recipe (Schema.org Recipe)
 */
export function generateRecipeStructuredData(recipe: {
  name: string
  description?: string
  image_url?: string | null
  prep_time?: string
  cook_time?: string
  servings?: number
  ingredients?: Array<{ name: string; quantity?: string | number; measurement?: string }>
  directions?: Array<{ text: string }>
  author?: string
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Recipe',
    name: recipe.name,
    description: recipe.description || `Recipe for ${recipe.name}`,
    image: recipe.image_url || `${BASE_URL}/img/recipe-placeholder.png`,
    author: {
      '@type': 'Person',
      name: recipe.author || 'Shelf Community'
    },
    prepTime: recipe.prep_time ? toIsoDuration(recipe.prep_time) : undefined,
    cookTime: recipe.cook_time ? toIsoDuration(recipe.cook_time) : undefined,
    recipeYield: recipe.servings ? `${recipe.servings} servings` : undefined,
    recipeIngredient: recipe.ingredients?.map(i =>
      `${i.quantity || ''} ${i.measurement || ''} ${i.name}`.trim()
    ),
    recipeInstructions: recipe.directions?.map((d, i) => ({
      '@type': 'HowToStep',
      position: i + 1,
      text: d.text
    }))
  }
}

/**
 * Generate BreadcrumbList structured data
 */
export function generateBreadcrumbStructuredData(
  items: Array<{ name: string; path: string }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${BASE_URL}${item.path}`
    }))
  }
}

/**
 * Generate SoftwareApplication structured data for app stores
 */
export function generateAppStructuredData() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Shelf',
    operatingSystem: 'Web, iOS, Android',
    applicationCategory: 'LifestyleApplication',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD'
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '150'
    }
  }
}
